import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getMatrixRecognition, MatrixBestPosition } from '../../api/assets';
import { formatTime } from '../../utils/time';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import { viewUrl } from '../../utils/imgUrl';
import { useProcessTypeText } from '../../utils/dictOptions';
import { FeedbackCard, FeedbackItem, LinkedMaterial, MaterialSourceBadge, RatingBadge } from './parts';
import { MatrixGridOverlay, MatrixStatusBadge } from './matrixParts';
import { CELL_TYPE_STYLE, MatrixCell, MatrixProfile, cellStyle } from './matrixUtils';

// 矩阵识别详情页 (/assets/matrix-recognitions/:taskId): 直连 prism matrix_tasks.
//
// result 两种落库形状都要兜 (prism 侧同样处理):
//   - 新任务: 算法字段 (grid / markers / x_axis_values / y_axis_values / denoised_image_url …) 在顶层,
//     VLM 打分在 result.quality_evaluation.{annotations, best_position, recommended_parameters,
//     parameter_profiles, summary, notes, precautions};
//   - 旧存量: 上面这些打分字段直接在 result 顶层.

type AnyMap = Record<string, unknown>;

const AXIS_LABEL: Record<string, string> = { power: '功率', speed: '速度', pass: '次数', frequency: '频率', density: '密度' };

const asArr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');

function MatrixRecognitionDetail() {
    const craftText = useProcessTypeText();
    const { id } = useParams<{ id: string }>();
    const [data, setData] = useState<Awaited<ReturnType<typeof getMatrixRecognition>> | null>(null);
    const [error, setError] = useState('');
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        getMatrixRecognition(id)
            .then((r) => { if (alive) setData(r); })
            .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : String(e)); });
        return () => { alive = false; };
    }, [id]);

    const d = data?.detail;
    const view = useMemo(() => {
        const result = (d?.result || {}) as AnyMap;
        const qe = (result.quality_evaluation || {}) as AnyMap;
        const pick = (k: string) => qe[k] ?? result[k];
        const cells = asArr<MatrixCell>(pick('annotations'));
        return {
            cells,
            profiles: asArr<MatrixProfile>(pick('parameter_profiles')),
            best: (pick('best_position') as MatrixBestPosition | null) ?? cells.find((c) => c.type === 'best') ?? null,
            rec: (pick('recommended_parameters') as { power?: number; speed?: number; pass?: number } | null) ?? null,
            summary: asStr(pick('summary')),
            notes: asArr<string>(pick('notes')),
            precautions: asArr<string>(pick('precautions')),
            image: asStr(result.denoised_image_url) || d?.image_url || '',
            xValues: asArr<number>(result.x_axis_values),
            yValues: asArr<number>(result.y_axis_values),
            costMs: typeof result.processing_time_ms === 'number' ? result.processing_time_ms : null,
        };
    }, [d]);
    const input = (d?.request_input || {}) as AnyMap;
    const feedbacks = useMemo(() => (data?.feedback || []) as FeedbackItem[], [data]);
    const rating = feedbacks[0]?.overall_rating || '';

    if (error) return <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>;
    if (!d) return <div className="text-center text-slate-400 text-sm py-16">加载中…</div>;

    // 轴: 新任务入参里有 col_key / row_key (列 = x 轴, 行 = y 轴); 存量没有, 回退「列 / 行」.
    const colKey = asStr(input.col_key).toLowerCase();
    const rowKey = asStr(input.row_key).toLowerCase();
    const xLabel = AXIS_LABEL[colKey] || asStr(input.col_key) || '列';
    const yLabel = AXIS_LABEL[rowKey] || asStr(input.row_key) || '行';
    const nRows = Number(input.n_rows) || (view.yValues.length || null);
    const nCols = Number(input.n_cols) || (view.xValues.length || null);
    const processType = asStr(input.process_type);
    const rowTitle = asStr(input.row_title);
    const { best, rec } = view;

    const meta: [string, React.ReactNode][] = [
        ['状态', <span key="s" className="inline-flex items-center gap-1.5"><MatrixStatusBadge status={d.status} /><span className="font-mono text-[11px] text-slate-400">{d.raw_status}{d.progress_stage ? ` · ${d.progress_stage}` : ''}</span></span>],
        ['设备', d.device_name
            ? <span key="d">{d.device_name} <span className="font-mono text-[11px] text-slate-400">{d.device_model}</span></span>
            : <span key="d" className="font-mono">{d.device_model || '—'}</span>],
        ['用户', <span key="u" className="font-mono">{d.uid || '—'}</span>],
        ['矩阵', nRows && nCols ? `${nRows} 行 × ${nCols} 列（行=${yLabel}，列=${xLabel}）` : '—'],
        ['工艺', processType ? craftText(processType, processType) : '—'],
        ['识别耗时', view.costMs != null ? `${(view.costMs / 1000).toFixed(1)} s` : '—'],
        ['完成于', formatTime(d.completed_at || undefined)],
    ];

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-xs text-slate-400 mb-1">
                        <Link to="/assets/matrix-recognitions" className="text-brand-600 hover:underline">← 返回 矩阵识别</Link>
                    </div>
                    <div className="text-base font-semibold truncate">{d.material_name || d.material?.name?.zh || d.material?.name?.en || '未填写材料'}</div>
                    <div className="text-xs text-slate-500 mt-1">
                        {d.device_name || d.device_model || '—'} · 上传于 {formatTime(d.created_at)}
                        <span className="ml-2 font-mono text-slate-400">{d.task_id}</span>
                    </div>
                </div>
                {rating && <RatingBadge rating={rating} />}
            </div>

            <div className="grid grid-cols-[320px_1fr] gap-5 items-start">
                {/* 左: 任务信息 + 溯源 + 反馈 */}
                <div className="space-y-3">
                    <div className="card p-3 text-xs space-y-1.5">
                        <div className="text-xs font-semibold text-slate-500 mb-1">任务信息</div>
                        {meta.map(([k, v]) => (
                            <div key={k} className="flex gap-2">
                                <span className="w-16 shrink-0 text-slate-400">{k}</span>
                                <span className="text-slate-700 min-w-0 break-words">{v}</span>
                            </div>
                        ))}
                        {rowTitle && (
                            <div className="mt-2 pt-2 border-t border-slate-50 text-[11px] text-slate-500 whitespace-pre-line"
                                title="矩阵图上的标题 (Studio 生成测试图时写入, 含固定参数)">{rowTitle}</div>
                        )}
                    </div>
                    <div className="card p-3 text-xs text-slate-500 space-y-1.5">
                        <div className="text-xs font-semibold text-slate-500 mb-1">溯源</div>
                        <div className="flex items-start gap-2">
                            <span className="shrink-0">平台材料:</span>
                            {d.is_local ? (
                                <span className="flex items-center gap-2">
                                    <MaterialSourceBadge source="user_custom" />
                                    <span className="text-slate-400">用户本地素材</span>
                                </span>
                            ) : (
                                <LinkedMaterial material={d.material} source={d.material_source} />
                            )}
                        </div>
                        <div className="text-[11px] text-slate-400">
                            线索: 材料 id <span className="font-mono">{d.material_id || '—'}</span>
                            {d.is_local != null && ` · ${d.is_local ? '本地素材' : 'XCS 材料'}`}
                        </div>
                    </div>
                    <FeedbackCard items={feedbacks} onZoom={setZoomSrc} />
                </div>

                {/* 右: 网格打分 + 最佳推荐 + 档案 + AI 总结 */}
                <div className="space-y-4">
                    {d.status === 'failed' && d.error_message && (
                        <div className="card p-3 text-xs text-rose-700 border-rose-200 bg-rose-50 break-words">
                            <b>失败原因:</b> {d.error_label || d.error_message}
                            {d.error_label && (
                                <div className="mt-1 font-mono text-[11px] text-rose-500/80">{d.error_code} · {d.error_message}</div>
                            )}
                        </div>
                    )}

                    <div className="card p-4 space-y-3">
                        <div className="flex items-baseline gap-2">
                            <div className="text-xs font-semibold text-slate-500">网格识别</div>
                            <div className="text-[11px] text-slate-400">每格按评级着色，数字为 Score；悬停看该格参数与 AI 评语</div>
                        </div>
                        {view.image ? (
                            <MatrixGridOverlay src={view.image} cells={view.cells} profiles={view.profiles} onZoom={setZoomSrc} />
                        ) : (
                            <div className="w-full h-40 rounded-lg bg-slate-100" />
                        )}
                        {view.cells.length > 0 && (
                            <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
                                {Object.entries(CELL_TYPE_STYLE).map(([t, st]) => (
                                    <span key={t} className="inline-flex items-center gap-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: st.bg }} />{st.label}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {(best || rec) && (
                        <div className="card p-4">
                            <div className="text-xs font-semibold text-slate-500 mb-2">最佳推荐</div>
                            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 text-sm">
                                <div className="grid grid-cols-5 gap-3">
                                    {[
                                        ['功率', best?.power ?? rec?.power],
                                        ['速度', best?.speed ?? rec?.speed],
                                        ['次数', rec?.pass],
                                        ['行 / 列', best ? `R${best.row} / C${best.col}` : undefined],
                                        ['Score', best?.score],
                                    ].map(([k, v]) => (
                                        <div key={String(k)}>
                                            <div className="text-[10px] text-slate-400">{k}</div>
                                            <div className="font-mono text-slate-800">{v ?? '—'}</div>
                                        </div>
                                    ))}
                                </div>
                                {best?.description && <div className="mt-2 text-xs text-slate-600">{best.description}</div>}
                            </div>
                        </div>
                    )}

                    {view.profiles.length > 0 && (
                        <div className="card p-4">
                            <div className="text-xs font-semibold text-slate-500 mb-2">参数档案 ({view.profiles.length})</div>
                            <div className="grid grid-cols-3 gap-3">
                                {view.profiles.map((p, i) => (
                                    <div key={p.key ?? i} className="rounded-xl border border-slate-100 p-3 text-xs space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-slate-700">{p.name || p.tag || p.key}</span>
                                            {p.tag && <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 text-[10px]">{p.tag}</span>}
                                        </div>
                                        <div className="font-mono text-slate-600">
                                            功率 {p.power ?? '—'} · 速度 {p.speed ?? '—'}
                                            {p.row != null && p.col != null && <span className="text-slate-400"> · R{p.row}/C{p.col}</span>}
                                        </div>
                                        {p.description && <div className="text-slate-500">{p.description}</div>}
                                        {p.applicable_scenario && <div className="text-[11px] text-slate-400">适用: {p.applicable_scenario}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {(view.summary || view.notes.length > 0 || view.precautions.length > 0) && (
                        <div className="card p-4 space-y-3 text-xs">
                            {view.summary && (
                                <div>
                                    <div className="font-semibold text-slate-500 mb-1">AI 总结</div>
                                    <p className="text-sm leading-relaxed text-slate-700">{view.summary}</p>
                                </div>
                            )}
                            {view.notes.length > 0 && (
                                <div>
                                    <div className="font-semibold text-slate-500 mb-1">备注</div>
                                    <ul className="list-disc pl-5 text-slate-600 space-y-0.5">{view.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
                                </div>
                            )}
                            {view.precautions.length > 0 && (
                                <div>
                                    <div className="font-semibold text-slate-500 mb-1">注意事项</div>
                                    <ul className="list-disc pl-5 text-amber-700 space-y-0.5">{view.precautions.map((n, i) => <li key={i}>{n}</li>)}</ul>
                                </div>
                            )}
                        </div>
                    )}

                    {view.cells.length > 0 && (
                        <details className="card p-4 text-xs">
                            <summary className="cursor-pointer font-semibold text-slate-500">全部 {view.cells.length} 格打分</summary>
                            <div className="mt-2 overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="text-left text-[10px] text-slate-400 border-b border-slate-100">
                                            <th className="py-1.5 pr-3 whitespace-nowrap">行/列</th>
                                            <th className="py-1.5 pr-3 text-right whitespace-nowrap">功率</th>
                                            <th className="py-1.5 pr-3 text-right whitespace-nowrap">速度</th>
                                            <th className="py-1.5 pr-3 text-right whitespace-nowrap">Score</th>
                                            <th className="py-1.5 pr-3 whitespace-nowrap">评级</th>
                                            <th className="py-1.5">AI 评语</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {view.cells.map((c) => {
                                            const st = cellStyle(c.type);
                                            return (
                                                <tr key={c.id ?? `${c.row}-${c.col}`} className="border-b border-slate-50 last:border-0">
                                                    <td className="py-1.5 pr-3 font-mono text-slate-500">R{c.row}/C{c.col}</td>
                                                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{c.power ?? '—'}</td>
                                                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{c.speed ?? '—'}</td>
                                                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-bold">{c.score ?? '—'}</td>
                                                    <td className="py-1.5 pr-3">
                                                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                                                            style={{ background: st.bg, color: c.type === 'best' || c.type === 'good' ? '#fff' : '#1e293b' }}>{st.label}</span>
                                                    </td>
                                                    <td className="py-1.5 text-slate-500">{c.description || ''}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    )}

                    {view.cells.length === 0 && !best && !view.summary && d.status !== 'failed' && (
                        <div className="card p-6 text-center text-sm text-slate-400">无网格数据（任务可能未完成）</div>
                    )}
                </div>
            </div>

            <ImageLightbox src={viewUrl(zoomSrc)} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default MatrixRecognitionDetail;
